/**
 * Template detail view (S24 polish) — fills the add-tile dialog's right half
 * while the Templates tab has a selected template, which used to sit empty
 * (the type cards are meaningless before a query exists). Shows the whole
 * story before anything is inserted: full description, the SQL it will drop
 * into the editor, the grants it needs, and the tile shape it fits.
 *
 * "Use template" is the ONLY insertion trigger and calls straight back into
 * the dialog's `handleUseTemplate` → `useTemplateInsertion.insertTemplate`,
 * which stays the single insertion path (SQL as editable text, tile type +
 * title pre-fit, back to the SQL tab, auto-preview).
 */
import type { ReactNode } from 'react';
import { ArrowLeft, Lock, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import type { DashboardTemplate } from '../../templates/templateTypes';
import { splitTemplateDescription } from './templateDescription';
import { CATEGORY_LABELS, FIT_LABELS } from './templateLabels';

function Chip({ children }: { children: ReactNode }) {
  return (
    <span className="rounded-sm border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
      {children}
    </span>
  );
}

export function TemplateDetailPane({
  template,
  onUse,
  onBack,
}: {
  template: DashboardTemplate;
  /** Runs the dialog's single insertion path. */
  onUse: (template: DashboardTemplate) => void;
  /** Clears the selection — the config pane comes back. */
  onBack: () => void;
}) {
  const { track } = useAnalytics();
  const { lead, requirement } = splitTemplateDescription(template.description);

  return (
    <div className="flex min-h-0 flex-col gap-2.5" data-testid="template-detail-pane">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-foreground" data-testid="template-detail-title">
            {template.title}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-1">
            <Chip>{CATEGORY_LABELS[template.category]}</Chip>
            <Chip>
              <span className="inline-flex items-center gap-1">
                <Sparkles className="h-2.5 w-2.5" aria-hidden />
                Fits {FIT_LABELS[template.defaultTileType]}
              </span>
            </Chip>
            {template.minimumVersion && <Chip>Needs {template.minimumVersion}+</Chip>}
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 shrink-0 gap-1 px-2 text-xs text-muted-foreground"
          onClick={onBack}
          data-testid="template-detail-back"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          Back
        </Button>
      </div>

      <p className="text-xs leading-relaxed text-muted-foreground" data-testid="template-detail-description">
        {lead}
      </p>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">Permissions</Label>
        <p
          className="flex items-start gap-1.5 text-[11px] leading-snug text-muted-foreground"
          data-testid="template-detail-permissions"
        >
          <Lock className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
          <span>{requirement ?? 'No special grant documented — your own session can run this.'}</span>
        </p>
      </div>

      <div className="flex min-h-0 flex-col gap-1">
        <Label className="text-xs text-muted-foreground">SQL</Label>
        <pre
          className="max-h-56 min-h-0 overflow-auto rounded-md border border-border bg-muted/40 p-2 font-mono text-[10.5px] leading-relaxed text-muted-foreground"
          data-testid="template-detail-sql"
        >
          {template.sql}
        </pre>
      </div>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          className="h-7 text-xs"
          onClick={() => {
            track('feature:dashboard_template_use', {
              engine: template.engine,
              category: template.category,
              tile_type: template.defaultTileType,
            });
            onUse(template);
          }}
          data-testid="template-detail-use"
        >
          Use template
        </Button>
        <span className="text-[11px] text-muted-foreground">
          Inserts as editable SQL and runs a read-only preview.
        </span>
      </div>
    </div>
  );
}
